import { askConfirm } from '@/components/ui/ConfirmHost';
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getGuardians, postGuardian, putGuardian, getParentLinkRequests,
  approveParentLink, rejectParentLink, getDashboard, getStudents,
  linkGuardianToStudent, deleteGuardianStudent,
} from '@/api/owner';
import {
  Plus, Users, Edit2, CheckCircle2, XCircle, X, UserPlus,
  UserMinus, Eye,
} from 'lucide-react';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};

const RELATIONSHIPS = ['father', 'mother', 'guardian', 'other'];

interface LinkedStudent {
  student: any;
  relationship: string;
  isPrimary: boolean;
}

export function FamiliesPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'guardians' | 'requests'>('guardians');
  const [search, setSearch] = useState('');
  const [relationshipFilter, setRelationshipFilter] = useState<string>('');
  const [modal, setModal] = useState<
    | null
    | { kind: 'add' }
    | { kind: 'edit'; guardian: any }
    | { kind: 'link'; guardian: any }
    | { kind: 'detail'; guardian: any }
  >(null);

  const dashQ = useQuery({ queryKey: ['owner-dashboard'], queryFn: () => getDashboard().then((r: any) => r.data?.data ?? r.data), staleTime: 60_000 });
  const guardiansQ = useQuery({ queryKey: ['owner-guardians'], queryFn: () => getGuardians().then((r: any) => r.data?.data ?? r.data), enabled: tab === 'guardians' });
  const requestsQ = useQuery({ queryKey: ['owner-parent-link-requests'], queryFn: () => getParentLinkRequests().then((r: any) => r.data?.data ?? r.data), enabled: tab === 'requests' });
  // Fetch students to build the reverse map (guardian -> linked students)
  const studentsQ = useQuery({ queryKey: ['owner-students'], queryFn: () => getStudents({ per_page: 500 }).then((r: any) => r.data?.data ?? r.data) });

  const dash = dashQ.data as any;
  const guardians = arr<any>(guardiansQ.data);
  const requests = arr<any>(requestsQ.data);
  const students = arr<any>(studentsQ.data);

  const label = [dash?.school?.name, dash?.session?.name, dash?.term?.name].filter(Boolean).join(' · ');

  // Build reverse map: guardianId -> [{ student, relationship, isPrimary }]
  const studentGuardiansMap = useMemo(() => {
    const m: Record<number, LinkedStudent[]> = {};
    students.forEach((s: any) => {
      const gs = arr<any>(s.guardians);
      gs.forEach((g: any) => {
        if (!g.id) return;
        if (!m[g.id]) m[g.id] = [];
        m[g.id].push({
          student: s,
          relationship: g.relationship ?? g.pivot?.relationship ?? '—',
          isPrimary:
            g.is_primary === 1 || g.is_primary === true ||
            g.pivot?.is_primary === 1 || g.pivot?.is_primary === true,
        });
      });
    });
    return m;
  }, [students]);

  // Prefer the direct `students` array on the guardian response if it exists,
  // otherwise fall back to the reverse map.
  const linkedFor = (g: any): LinkedStudent[] => {
    const direct = arr<any>(g.students ?? g.linked_students);
    if (direct.length > 0) {
      return direct.map((s: any) => ({
        student: s,
        relationship: s.pivot?.relationship ?? s.relationship ?? '—',
        isPrimary:
          s.pivot?.is_primary === 1 || s.pivot?.is_primary === true ||
          s.is_primary === 1 || s.is_primary === true,
      }));
    }
    return studentGuardiansMap[g.id] ?? [];
  };

  // Relationship shown in the row — derive from guardian row, then most common linked relationship
  const relFor = (g: any) => {
    if (g.relationship) return g.relationship;
    const linked = linkedFor(g);
    if (linked.length === 0) return '—';
    const counts: Record<string, number> = {};
    linked.forEach((l) => { counts[l.relationship] = (counts[l.relationship] ?? 0) + 1; });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted[0]?.[0] ?? '—';
  };

  const isPrimaryPayer = (g: any) => {
    if (g.is_primary === 1 || g.is_primary === true) return true;
    return linkedFor(g).some((l) => l.isPrimary);
  };

  const filtered = guardians.filter((g: any) => {
    const q = search.toLowerCase();
    const matchesSearch = !q || `${g.first_name ?? ''} ${g.last_name ?? ''} ${g.phone ?? ''} ${g.email ?? ''}`.toLowerCase().includes(q);
    if (!matchesSearch) return false;
    if (relationshipFilter) {
      const rel = relFor(g);
      if (rel !== relationshipFilter) return false;
    }
    return true;
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['owner-guardians'] });
    qc.invalidateQueries({ queryKey: ['owner-parent-link-requests'] });
    qc.invalidateQueries({ queryKey: ['owner-students'] });
    qc.invalidateQueries({ queryKey: ['owner-student'] });
    setModal(null);
  };

  const createM = useMutation({ mutationFn: (b: any) => postGuardian(b), onSuccess: refresh, onError: (e: any) => alert(e.response?.data?.message ?? 'Failed') });
  const updateM = useMutation({ mutationFn: (d: { id: number; body: any }) => putGuardian(d.id, d.body), onSuccess: refresh, onError: (e: any) => alert(e.response?.data?.message ?? 'Failed') });
  const approveM = useMutation({ mutationFn: (id: number) => approveParentLink(id), onSuccess: refresh, onError: (e: any) => alert(e.response?.data?.message ?? 'Failed') });
  const rejectM = useMutation({ mutationFn: (id: number) => rejectParentLink(id), onSuccess: refresh, onError: (e: any) => alert(e.response?.data?.message ?? 'Failed') });
  const linkM = useMutation({ mutationFn: (d: { guardianId: number; body: any }) => linkGuardianToStudent(d.guardianId, d.body), onSuccess: refresh, onError: (e: any) => alert(e.response?.data?.message ?? 'Failed') });
  const unlinkM = useMutation({ mutationFn: (d: { guardianId: number; studentId: number }) => deleteGuardianStudent(d.guardianId, d.studentId), onSuccess: refresh, onError: (e: any) => alert(e.response?.data?.message ?? 'Failed') });

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto pb-10 p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">{label || 'School · Loading…'}</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Families</h1>
          <p className="text-slate-500 text-sm mt-1">Manage guardians and parent accounts</p>
        </div>
        {tab === 'guardians' && (
          <button onClick={() => setModal({ kind: 'add' })} className="bg-[#0f172a] hover:bg-slate-800 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-sm flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add guardian
          </button>
        )}
      </div>

      <div className="flex gap-6 border-b border-slate-200">
        {(['guardians', 'requests'] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`pb-3 text-sm font-bold border-b-2 capitalize ${tab === t ? 'border-[#0f172a] text-[#0f172a]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            {t === 'guardians' ? `Guardians (${guardians.length})` : `Parent link requests (${requests.length})`}
          </button>
        ))}
      </div>

      {tab === 'guardians' && (
        <>
          <div className="flex flex-wrap gap-2">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, phone or email…" className="flex-1 min-w-[200px] border border-slate-300 rounded-lg px-4 py-2.5 text-sm bg-white" />
            <select value={relationshipFilter} onChange={(e) => setRelationshipFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm font-semibold bg-white">
              <option value="">All relationships</option>
              {RELATIONSHIPS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {guardiansQ.isLoading || studentsQ.isLoading ? <p className="p-8 text-center text-sm text-slate-500">Loading…</p> :
             filtered.length === 0 ? (
               <div className="p-16 text-center">
                 <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                 <p className="font-bold text-slate-700 mb-1">No guardians {search ? 'match your search' : 'yet'}</p>
                 <p className="text-sm text-slate-500">Add a guardian to start linking students</p>
               </div>
             ) : (
               <table className="w-full text-left text-sm">
                 <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                   <tr>
                     <th className="px-4 py-3">Guardian</th>
                     <th className="px-4 py-3">Phone</th>
                     <th className="px-4 py-3">Email</th>
                     <th className="px-4 py-3">Relationship</th>
                     <th className="px-4 py-3">Linked students</th>
                     <th className="px-4 py-3 text-right">Actions</th>
                   </tr>
                 </thead>
                 <tbody className="divide-y divide-slate-100">
                   {filtered.map((g: any) => {
                     const linked = linkedFor(g);
                     const rel = relFor(g);
                     const primary = isPrimaryPayer(g);
                     return (
                       <tr key={g.id} className="hover:bg-slate-50">
                         <td className="px-4 py-3">
                           <button onClick={() => setModal({ kind: 'detail', guardian: g })} className="text-left">
                             <div className="font-semibold text-slate-900 hover:text-brand-600">{g.first_name} {g.last_name}</div>
                             {primary && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-green-100 text-green-700 rounded mt-0.5 inline-block">PRIMARY</span>}
                           </button>
                         </td>
                         <td className="px-4 py-3 font-mono text-xs">{g.phone ?? '—'}</td>
                         <td className="px-4 py-3 text-slate-600 text-xs">{g.email ?? '—'}</td>
                         <td className="px-4 py-3 text-slate-600 text-xs capitalize">{rel}</td>
                         <td className="px-4 py-3">
                           {linked.length === 0 ? (
                             <span className="text-xs text-slate-400">None</span>
                           ) : (
                             <div className="flex flex-wrap gap-1">
                               {linked.slice(0, 3).map((l, i) => (
                                 <span key={i} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded" title={`${l.relationship}${l.isPrimary ? ' · Primary' : ''}`}>
                                   {l.student.first_name} {l.student.last_name}
                                   {l.isPrimary && ' ★'}
                                 </span>
                               ))}
                               {linked.length > 3 && <span className="text-xs text-slate-500">+{linked.length - 3} more</span>}
                             </div>
                           )}
                         </td>
                         <td className="px-4 py-3 text-right space-x-1">
                           <button onClick={() => setModal({ kind: 'detail', guardian: g })} title="View details" className="text-xs font-semibold text-slate-600 hover:underline p-1">
                             <Eye className="w-3.5 h-3.5 inline" />
                           </button>
                           <button onClick={() => setModal({ kind: 'link', guardian: g })} title="Link student" className="text-xs font-semibold text-blue-600 hover:underline p-1">
                             <UserPlus className="w-3.5 h-3.5 inline" />
                           </button>
                           <button onClick={() => setModal({ kind: 'edit', guardian: g })} title="Edit" className="text-xs font-semibold text-brand-600 hover:underline p-1">
                             <Edit2 className="w-3.5 h-3.5 inline" />
                           </button>
                         </td>
                       </tr>
                     );
                   })}
                 </tbody>
               </table>
             )}
          </div>
        </>
      )}

      {tab === 'requests' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {requestsQ.isLoading ? <p className="p-8 text-center text-sm text-slate-500">Loading…</p> :
           requests.length === 0 ? (
             <div className="p-16 text-center">
               <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
               <p className="font-bold text-slate-700 mb-1">No pending requests</p>
               <p className="text-sm text-slate-500">When a parent asks to link a child, it will appear here</p>
             </div>
           ) : (
             <table className="w-full text-left text-sm">
               <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                 <tr><th className="px-4 py-3">Parent</th><th className="px-4 py-3">Student</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3 text-right">Actions</th></tr>
               </thead>
               <tbody className="divide-y divide-slate-100">
                 {requests.map((r: any) => (
                   <tr key={r.id} className="hover:bg-slate-50">
                     <td className="px-4 py-2 font-semibold text-slate-900">{r.guardian_name ?? r.parent_name ?? '—'}</td>
                     <td className="px-4 py-2 text-slate-600 text-xs">{r.student_name ?? r.student ?? '—'}</td>
                     <td className="px-4 py-2 font-mono text-xs">{r.phone ?? '—'}</td>
                     <td className="px-4 py-2 text-right space-x-1">
                       <button onClick={() => approveM.mutate(r.id)} disabled={approveM.isPending} className="text-xs px-2 py-1 bg-green-50 text-green-700 border border-green-200 rounded font-semibold">
                         <CheckCircle2 className="w-3 h-3 inline" /> Approve
                       </button>
                       <button onClick={() => rejectM.mutate(r.id)} disabled={rejectM.isPending} className="text-xs px-2 py-1 bg-red-50 text-red-700 border border-red-200 rounded font-semibold">
                         <XCircle className="w-3 h-3 inline" /> Reject
                       </button>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           )}
        </div>
      )}

      {(modal?.kind === 'add' || modal?.kind === 'edit') && (
        <GuardianModal
          guardian={modal.kind === 'edit' ? modal.guardian : null}
          onClose={() => setModal(null)}
          onSave={(body: any) => {
            if (modal.kind === 'edit') updateM.mutate({ id: modal.guardian.id, body });
            else createM.mutate(body);
          }}
          saving={createM.isPending || updateM.isPending}
        />
      )}

      {modal?.kind === 'link' && (
        <LinkStudentModal
          guardian={modal.guardian}
          students={students}
          alreadyLinked={linkedFor(modal.guardian).map((l) => l.student.id)}
          onClose={() => setModal(null)}
          onSave={(body: any) => linkM.mutate({ guardianId: modal.guardian.id, body })}
          saving={linkM.isPending}
        />
      )}

      {modal?.kind === 'detail' && (
        <GuardianDetailModal
          guardian={modal.guardian}
          linked={linkedFor(modal.guardian)}
          onClose={() => setModal(null)}
          onUnlink={async (studentId: number) => {
            if (await askConfirm('Unlink this student from this guardian?')) {
              unlinkM.mutate({ guardianId: modal.guardian.id, studentId });
            }
          }}
          onLink={() => setModal({ kind: 'link', guardian: modal.guardian })}
          onEdit={() => setModal({ kind: 'edit', guardian: modal.guardian })}
        />
      )}
    </div>
  );
}

function GuardianModal({ guardian, onClose, onSave, saving }: any) {
  const [first, setFirst] = useState(guardian?.first_name ?? '');
  const [last, setLast] = useState(guardian?.last_name ?? '');
  const [phone, setPhone] = useState(guardian?.phone ?? '');
  const [email, setEmail] = useState(guardian?.email ?? '');
  const [relationship, setRelationship] = useState(guardian?.relationship ?? 'guardian');
  const [address, setAddress] = useState(guardian?.address ?? '');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="font-bold text-slate-900">{guardian ? 'Edit guardian' : 'Add guardian'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave({
          first_name: first, last_name: last, phone, email: email || undefined,
          relationship, address: address || undefined, ...(!guardian && password ? { password } : {}),
        }); }} className="p-6 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-semibold mb-1">First name</label><input value={first} onChange={(e) => setFirst(e.target.value)} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            <div><label className="block text-sm font-semibold mb-1">Surname</label><input value={last} onChange={(e) => setLast(e.target.value)} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
          </div>
          <div><label className="block text-sm font-semibold mb-1">Phone</label><input value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="08030000000" className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
          <div><label className="block text-sm font-semibold mb-1">Email (optional)</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
          <div><label className="block text-sm font-semibold mb-1">Relationship</label>
            <select value={relationship} onChange={(e) => setRelationship(e.target.value)} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
              {RELATIONSHIPS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </div>
          <div><label className="block text-sm font-semibold mb-1">Address (optional)</label><textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
          {!guardian && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
              <label className="block text-sm font-semibold mb-1">Login password (optional)</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} autoComplete="new-password" placeholder="At least 6 characters" className="w-full border border-slate-300 rounded-lg p-2.5 pr-16 text-sm bg-white" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500 px-2">{showPw ? 'Hide' : 'Show'}</button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">They sign in with their phone{email ? ' or email' : ''} and this password straight away. Leave empty to give them a login later.</p>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">{saving ? 'Saving…' : guardian ? 'Save' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LinkStudentModal({ guardian, students, alreadyLinked, onClose, onSave, saving }: any) {
  const [studentId, setStudentId] = useState<number | ''>('');
  const [relationship, setRelationship] = useState('guardian');
  const [isPrimary, setIsPrimary] = useState(false);
  const [canPay, setCanPay] = useState(true);

  const available = students.filter((s: any) => !alreadyLinked.includes(s.id));

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <div>
            <h3 className="font-bold text-slate-900">Link student</h3>
            <p className="text-xs text-slate-500 mt-0.5">to {guardian.first_name} {guardian.last_name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (!studentId) return; onSave({
          student_id: Number(studentId), relationship, is_primary: isPrimary, can_pay: canPay,
        }); }} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Student</label>
            <select value={studentId} onChange={(e) => setStudentId(e.target.value ? Number(e.target.value) : '')} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
              <option value="">Choose student…</option>
              {available.map((s: any) => (
                <option key={s.id} value={s.id}>{s.first_name} {s.last_name} · {s.admission_no}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">{available.length === 0 ? 'All students already linked to this guardian' : `${available.length} student(s) available`}</p>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Relationship</label>
            <select value={relationship} onChange={(e) => setRelationship(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
              {RELATIONSHIPS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} className="w-4 h-4" />
              <span className="font-semibold text-slate-800">Make this the primary payer</span>
            </label>
            <p className="text-xs text-slate-600 mt-1">Only one guardian per student can be primary.</p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={canPay} onChange={(e) => setCanPay(e.target.checked)} className="w-4 h-4" />
            <span className="text-slate-700">Guardian can pay fees</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">Cancel</button>
            <button type="submit" disabled={saving || !studentId} className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">{saving ? 'Linking…' : 'Link student'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GuardianDetailModal({ guardian, linked, onClose, onUnlink, onLink, onEdit }: any) {
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <div>
            <h3 className="font-bold text-slate-900">{guardian.first_name} {guardian.last_name}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{guardian.phone}{guardian.email ? ` · ${guardian.email}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-slate-500 text-xs">Phone</span><div className="font-semibold">{guardian.phone ?? '—'}</div></div>
            <div><span className="text-slate-500 text-xs">Email</span><div className="font-semibold">{guardian.email ?? '—'}</div></div>
            <div><span className="text-slate-500 text-xs">Relationship</span><div className="font-semibold capitalize">{guardian.relationship ?? '—'}</div></div>
            <div><span className="text-slate-500 text-xs">Linked students</span><div className="font-semibold">{linked.length}</div></div>
          </div>

          {guardian.address && (
            <div><span className="text-xs text-slate-500">Address</span><div className="text-sm text-slate-700 mt-0.5">{guardian.address}</div></div>
          )}

          <div className="border-t border-slate-200 pt-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-bold text-slate-900 text-sm">Linked students</h4>
              <button onClick={onLink} className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" /> Link student</button>
            </div>
            {linked.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center border-2 border-dashed border-slate-200 rounded-lg">No students linked yet</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {linked.map((l: LinkedStudent, i: number) => (
                  <li key={i} className="py-2 flex justify-between items-center">
                    <div>
                      <div className="font-semibold text-sm text-slate-900">
                        {l.student.first_name} {l.student.last_name}
                        {l.isPrimary && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 bg-green-100 text-green-700 rounded">PRIMARY</span>}
                      </div>
                      <div className="text-xs text-slate-500">
                        {l.student.admission_no} · {l.student.class ?? '—'} · {l.relationship}
                      </div>
                    </div>
                    <button onClick={() => onUnlink(l.student.id)} className="text-xs font-semibold text-red-600 hover:underline p-1">
                      <UserMinus className="w-3.5 h-3.5 inline" /> Unlink
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex gap-2 pt-2 border-t border-slate-200">
            <button onClick={onEdit} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm flex items-center justify-center gap-1">
              <Edit2 className="w-4 h-4" /> Edit details
            </button>
            <button onClick={onClose} className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm">Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}
